/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable @typescript-eslint/no-shadow */
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useCallback, useEffect } from "react"
import { Row, Col, Flex } from "@duxton-web/ui"
import { useSelector } from "react-redux"
import { isEmpty } from "lodash"
import { AppState } from "store/store.type"
import { TroyWebTracker } from "utils/tracking"
import { MENUS, CATALOGUES } from "constants/routes"
import { useHistory, useLocation, Redirect } from "react-router-dom"
import { MenuStoreListSearch } from "components/search/MenuStoreListSearch"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import {
  WithMenuHeader,
  WithMenuHeaderInjectedProps,
} from "components/hoc/WithMenuHeader"
import {
  EventAction,
  EventActionEng,
  EventCategory,
  EventCategoryEng,
  FilterTrigger,
} from "constants/tracking"
import {
  getMerchantCombinedType,
  CombinedType,
  isMartStore as isMartStoreHelper,
} from "utils/menu_store_helper"
import { StoreList } from "components/list/StoreList"
import { MerchantStoreInfo, OutletStore, StoreFilter } from "types/models/menu"
import useSingleStoreUser from "hooks/useSingleStoreUser"
import LazyLoadWrapper from "components/LazyLoadWrapper"
import ItemPhotoCoverageBanner from "components/banners/ItemPhotoCoverageBanner"
import { Config } from "constants/config"
import {
  SorterResult,
  TableRowSelection,
} from "@duxton-web/ui/es/table/interface"
import {
  useFetchItemsWithoutPhotoCountQuery,
  useFetchMerchantAggregateQuery,
} from "queries/menuApi"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { MerchantAggregate } from "types/api"
import { getMenuEditorDeepLinkPath } from "utils/menu_helper"
import { menuStoreListHeaderPadding, menuStoreListStyle } from "../style"
import useMenuStoreList from "./hooks/useMenuStoreList"

interface MenuStoreListProps {
  title?: string
  rowSelection?: TableRowSelection<OutletStore>
  className?: string
  hideSearchBox?: Boolean
  hideStoreNameArrowRight?: boolean
  outletsOverride?: MerchantStoreInfo[]
  onRowClickOverride?: (outlet: OutletStore) => void
  eventCategory?: EventCategory
  eventLabels?: { [key: string]: string }
  itemsWithoutPhotoCount?: number
  rowClassName?: (record: OutletStore) => string
  disableMenuV1?: boolean
}

export const MenuStoreList: FC<MenuStoreListProps> = ({
  title,
  rowSelection,
  className,
  hideSearchBox,
  outletsOverride,
  hideStoreNameArrowRight,
  onRowClickOverride,
  eventCategory = EventCategory.menuHomePage,
  eventLabels = {},
  itemsWithoutPhotoCount,
  rowClassName,
  disableMenuV1,
}: MenuStoreListProps) => {
  const {
    menu: { outlets: OutletsData, isFetchingOutletsSearch },
  } = useSelector((state: AppState) => state)

  const outlets = outletsOverride || OutletsData

  const history = useHistory()
  const location = useLocation()

  const {
    handleSearchFilter,
    handleFilter,
    filter,
    filteredList,
    handleLoadMoreOutlets,
    showLoadMore,
    cityList,
  } = useMenuStoreList(outletsOverride, disableMenuV1)

  const getStoreLink = (outlet: OutletStore) => {
    const basePath = `${isMartStoreHelper(outlet) ? CATALOGUES : MENUS}/${
      outlet?.merchantID
    }`

    return getMenuEditorDeepLinkPath({
      basePath,
      search: location.search,
    })
  }

  const textMap = useTranslationsMap({
    placeHolderSearchStore: "menu.menuGroup.create.searchStoreName",
  })

  const handleAutocompleteSelect = (
    rank: number,
    _merchantID: string,
    merchantName: string,
    searchCategory: string,
  ) => {
    handleSearchFilter(merchantName, {
      trigger: FilterTrigger.autoCompleteSelect,
      category: eventCategory,
    })
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.selectStoreSearchresult,
      labels: {
        SEARCH_TEXT: merchantName,
        SEARCH_RESULT_RANK: rank,
        SEARCH_CATEGORY: searchCategory ?? null,
        STORE_ID: _merchantID,
        ...eventLabels,
      },
    })
  }

  const handleRowStoreList = (outlet: OutletStore) => ({
    onClick: () => {
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.clickStoreName,
        labels: {
          STORE_ID: outlet.merchantID,
          ...eventLabels,
        },
      })

      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.clickStoreSearchResult,
        labels: {
          FILTER_TEXT: filter.search,
          FILTER_ASC: filter.asc ?? true,
          FILTER_CITY: filter.city,
          STORE_ID: outlet.merchantID,
          STORE_NAME: outlet.merchantName,
        },
      })

      if (onRowClickOverride) {
        onRowClickOverride(outlet)
        return
      }
      history.push(getStoreLink(outlet))
    },
  })

  const handleOnClickAutocomplete = () => {
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.clickStoreSearch,
      labels: eventLabels,
    })
  }

  const handleCityFilterVisibleChange = (visible: boolean) => {
    if (visible) {
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.clickFilterCity,
        labels: eventLabels,
      })
    }
  }

  const handleSetFilter = (filter: StoreFilter) => {
    handleFilter(filter, {
      category: eventCategory,
      trigger: FilterTrigger.cityFilter,
    })
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.selectFilterCity,
      labels: {
        CITY_NAME: filter?.city?.toString(),
        ...eventLabels,
      },
    })
  }

  useEffect(() => {
    handleFilter(filter, {
      category: eventCategory,
      trigger: FilterTrigger.menuList,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleFilter])

  const handleOnChange = (
    pagination: any,
    filters: any,
    sorter: SorterResult<MerchantStoreInfo>,
  ) => {
    if (isEmpty(sorter) || sorter.field === "merchantName") {
      handleFilter(
        {
          ...filter,
          asc: sorter?.order ? sorter?.order === "ascend" : undefined,
        },
        {
          category: eventCategory,
          trigger: FilterTrigger.sort,
        },
      )
    }
  }

  const handleSearch = (value: string) => {
    handleSearchFilter(value, {
      trigger: FilterTrigger.searchInput,
      category: eventCategory,
    })
  }

  useEffect(() => {
    if (outlets && outlets?.length > 0) {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.storeList,
        action: EventActionEng.showStoreList,
        labels: {
          TOTAL_STORE: outlets.length,
        },
      })
    }
  }, [outlets])

  return (
    <Fragment>
      <div css={menuStoreListStyle} className={className}>
        <Row justify="space-between" align="middle">
          <Col>
            <h2 className="page-subtitle">
              {title ?? useTranslation("menu.stores.title")}
            </h2>
          </Col>
          {!hideSearchBox && (
            <Col>
              <MenuStoreListSearch
                data-testid="store-list-search"
                onSearch={handleSearch}
                onSelect={handleAutocompleteSelect}
                onClick={() => handleOnClickAutocomplete()}
                placeholder={textMap.placeHolderSearchStore}
                listSource={filteredList}
                isLoading={isFetchingOutletsSearch}
              />
            </Col>
          )}
        </Row>
        <Row
          css={css`
            margin-top: 32px;
          `}
        >
          <StoreList
            onRow={handleRowStoreList}
            rowSelection={rowSelection}
            dataSource={filteredList}
            onSelectCityFilter={handleSetFilter}
            onChangeFilterCityVisible={handleCityFilterVisibleChange}
            hideStoreNameArrowRight={hideStoreNameArrowRight}
            itemsWithoutPhotoCount={itemsWithoutPhotoCount}
            showLoadMore={showLoadMore}
            onLoadMore={handleLoadMoreOutlets}
            isLoading={isFetchingOutletsSearch}
            filter={filter}
            cityList={cityList}
            onChange={handleOnChange}
            rowClassName={rowClassName}
          />
        </Row>
      </div>
    </Fragment>
  )
}

export const MenuStoreListWithHeaderWrapped = ({
  setHeaderTitle,
}: WithMenuHeaderInjectedProps) => {
  const { isSingleStoreUser, storeID } = useSingleStoreUser()

  const {
    flags: {
      fetchItemsWithoutPhotoCount: isFetchItemsWithoutPhotoCountRtkEnabled,
      fetchMerchantAggregate: isFetchMerchantAggregateEnabled,
    },
  } = useRtkQueryMigrationEndpointsFlag()

  const {
    data: fetchItemsWithoutPhotoCountData,
  } = useFetchItemsWithoutPhotoCountQuery(undefined, {
    skip: !isFetchItemsWithoutPhotoCountRtkEnabled,
  })

  const {
    isMartStore,
    outlets,
    merchantBusinessTypeList: reduxMerchantBusinessTypeList,
    totalItemsWithoutPhoto: totalItemsWithoutPhotoRedux,
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const { data: merchantAggregateData } = useFetchMerchantAggregateQuery(
    {
      merchantGroupID,
      fields: [MerchantAggregate.cityID, MerchantAggregate.businessType],
    },
    { skip: !isFetchMerchantAggregateEnabled || !merchantGroupID },
  )

  const totalItemsWithoutPhoto = isFetchItemsWithoutPhotoCountRtkEnabled
    ? fetchItemsWithoutPhotoCountData?.totalItemsWithoutPhoto
    : totalItemsWithoutPhotoRedux

  const merchantBusinessTypeList = isFetchMerchantAggregateEnabled
    ? merchantAggregateData?.businessTypes ?? []
    : reduxMerchantBusinessTypeList

  const textMap = useTranslationsMap({
    titleMenu: "menu.title",
    titleCatalogue: "catalogue.title",
    titleMenuCatalogue: "catalogue.titleMenuCatalogue",
    itemPhotoCoverageBannerTitle: "itemPhotoCoverage.banner.title",
    itemPhotoCoverageBannerContent: "itemPhotoCoverage.banner.content",
  })

  const combinedType = getMerchantCombinedType(merchantBusinessTypeList)

  const location = useLocation()
  const history = useHistory()

  const bannerEmptyImage = require("../../../assets/images/menu/banner_empty_add_photo.svg")

  useEffect(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuHomePage,
      action: EventAction.pageView,
    })
  }, [])

  const handleFixPathname = () => {
    let newPathname = location.pathname

    /**
     * no need to update pathname as we'll redirect single store user to menu detail page
     */
    if (isSingleStoreUser) return

    if (combinedType === CombinedType.Catalogue)
      newPathname = location.pathname.replace(MENUS, CATALOGUES)
    if (combinedType === CombinedType.Menu)
      newPathname = location.pathname.replace(CATALOGUES, MENUS)

    if (location.pathname !== newPathname)
      history.replace({
        pathname: newPathname,
        search: location.search,
      })
  }

  const getTitle: () => string = () => {
    if (isEmpty(outlets))
      return isMartStore ? textMap.titleCatalogue : textMap.titleMenu

    if (combinedType === CombinedType.MenuCatalogue)
      return textMap.titleMenuCatalogue

    if (combinedType === CombinedType.Catalogue) return textMap.titleCatalogue

    return textMap.titleMenu
  }

  const updateHeader = () => setHeaderTitle(getTitle())

  useEffect(updateHeader, [outlets, combinedType])

  useEffect(handleFixPathname, [
    combinedType,
    history,
    isMartStore,
    isSingleStoreUser,
    location.pathname,
    location.search,
    outlets,
  ])
  if (isSingleStoreUser) {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.storeList,
      action: EventActionEng.showStoreList,
      labels: {
        TOTAL_STORE: 1,
      },
    })
  }
  const trackBannerView = useCallback(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupHomePage,
      action: EventAction.default,
      labels: {
        AR_LOCATION_STATENAME: EventCategory.menuGroupHomePage,
        NUDGE_ID: "MISSING_PHOTO_NUDGE",
        VIEW_ID: "MISSING_PHOTO_NUDGE",
        PRIORITY_IN_GROUP: "",
        SUMMARY_ORDER: "",
        TITLE_TRANSLATION_KEY:
          "mex-insights-menu-card-heading-items-with-no-photo",
        METRIC: "photo_coverage",
      },
    })
  }, [])

  return (
    <div css={menuStoreListHeaderPadding}>
      {isSingleStoreUser ? (
        <Redirect
          to={getMenuEditorDeepLinkPath({
            basePath: `${MENUS}/${storeID}`,
            search: location.search,
          })}
        />
      ) : (
        <Flex vertical gap={24}>
          {Config.enableItemPhotoCoverage && !!totalItemsWithoutPhoto && (
            <LazyLoadWrapper>
              <ItemPhotoCoverageBanner
                image={bannerEmptyImage}
                title={textMap.itemPhotoCoverageBannerTitle}
                content={textMap.itemPhotoCoverageBannerContent}
                trackPageView={trackBannerView}
              />
            </LazyLoadWrapper>
          )}

          <MenuStoreList itemsWithoutPhotoCount={totalItemsWithoutPhoto} />
        </Flex>
      )}
    </div>
  )
}

export const MenuStoreListWithHeader = WithMenuHeader()(
  MenuStoreListWithHeaderWrapped,
)
